// TraineeService — migrated to the OpenAPI-generated client.
//
// Every server call goes through the generated SDK (import from '../api/generated'). The
// auth (Bearer) + X-Timezone headers are injected centrally by configureApi's request
// interceptor, so there are no more per-call `localStorage.getItem("auth")` reads.
//
// Return-shape preservation: the legacy functions returned the RAW axios response, and
// every consumer reads `res.data` (the parsed HttpResponse envelope / body). The SDK's
// `data` == that parsed body, so we return `{data}` — an object whose `.data` is exactly
// what `res.data` was. Verified against every call site.
//
// IDOR-sweep note: several endpoints used to take a `companyId` param/query; the server
// removed those (company is derived from the authenticated principal). The generated
// types no longer accept companyId, so we DROP it from those calls (server ignored it
// anyway). Callers still pass a companyId arg — we simply don't forward it.
//
// Still on HttpService (flagged): the MULTIPART FILE UPLOAD (uploadUserImage). File uploads are an
// explicit migration exception.

import httpService from "./HttpService";
import {
  adminTraineeControllerGetTraineeStatistics,
  adminTraineeControllerGetEnhancedStatistics,
  adminTraineeControllerGetAll,
  adminTraineeControllerCheckEmailExists,
  adminTraineeControllerCreate,
  adminTraineeControllerGet,
  adminTraineeControllerEnableTrainees,
  adminTraineeControllerDisableTrainees,
  adminTraineeControllerUpdateTrainee,
  adminTraineeControllerResetPassword,
  adminTraineeControllerUpdateTraineeSettings,
  adminTraineeControllerUpdateTrainer,
  adminTraineeControllerAddSecondaryTrainer,
  adminTraineeControllerRemoveSecondaryTrainer,
  adminTraineeControllerDeleteUser,
  adminTraineeControllerBulkDeleteTrainees,
  adminTraineeControllerGetNutritionCalculatorData,
  adminTraineeControllerImportTrainees,
  adminWeightsControllerUpdateRow,
  adminWeightsControllerGetWeightTable,
  adminWeightsControllerGetTraineeWeights,
  adminTraineeNotesControllerCreate,
  adminTraineeNotesControllerUpdate,
  adminTraineeNotesControllerGetAll,
  adminTraineeNotesControllerDelete,
  adminTraineeTargetsControllerCreate,
  adminTraineeTargetsControllerUpdate,
  adminTraineeTargetsControllerGetAll,
  adminTraineeTargetsControllerDelete,
  adminClientReviewControllerGetLiveClientOverview,
} from "../api/generated";

export const getClientsStats = async (companyId) => {
  // companyId dropped — endpoint no longer takes it (derived from principal).
  const {data, error} = await adminTraineeControllerGetTraineeStatistics();
  if (error) throw error;
  return {data};
}


export const getClients = async (pageId = 0,
    pageSize = 20
    , keyword,
    trainer,
    gender,
    state,
    companyId,
    templateId = null) => {
  // Build the query conditionally, preserving the legacy filter semantics
  // (skip trainer when 0, skip gender/state when 'ALL'). companyId dropped —
  // the endpoint no longer accepts it (derived from the principal).
  const query = {pageId, pageSize};

  if (keyword) {
    query.keyword = keyword;
  }
  if (trainer !== 0) {
    query.trainerId = trainer;
  }
  if (gender !== 'ALL') {
    query.gender = gender;
  }
  if (state !== 'ALL') {
    query.state = state;
  }
  if (templateId !== null) {
    query.templateId = templateId;
  }

  const {data, error} = await adminTraineeControllerGetAll({query});
  if (error) throw error;
  return {data};
}

export const checkEmailExists = async (email) => {
  const {data, error} = await adminTraineeControllerCheckEmailExists({
    query: {email},
  });
  if (error) throw error;
  return {data};
}

export const createTrainee = async (body, companyId) => {
  // companyId dropped — endpoint no longer takes it (derived from principal).
  const {data, error} = await adminTraineeControllerCreate({body});
  if (error) throw error;
  return {data};
}

export const getClient = async (traineeId) => {
  const {data, error} = await adminTraineeControllerGet({path: {id: traineeId}});
  if (error) throw error;
  return {data};
}

export const enableClients = async (ids) => {
  const {data, error} = await adminTraineeControllerEnableTrainees({
    body: {ids},
  });
  if (error) throw error;
  return {data};
}

export const disableClients = async (ids) => {
  const {data, error} = await adminTraineeControllerDisableTrainees({
    body: {ids},
  });
  if (error) throw error;
  return {data};
}

// TODO(openapi-migration): multipart file upload (FormData) — stays on HttpService per the
// file-upload migration exception.
export const uploadUserImage = async (file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();

  // Update the formData object
  formData.append(
      "file",
      file,
      file.name
  );

  return await httpService.post(
      `admin/trainee/updateImage`,
      formData,
      token);
}

export const updateWeightTable = async (userId, row) => {
  const {data, error} = await adminWeightsControllerUpdateRow({
    path: {traineeId: userId},
    body: row,
  });
  if (error) throw error;
  return {data};
}

export const getClientWeightTable = async (userId, from, to) => {
  const {data, error} = await adminWeightsControllerGetWeightTable({
    path: {traineeId: userId},
    query: {from: from.unix(), to: to.unix()},
  });
  if (error) throw error;
  return {data};
}

export const getClientWeights = async (userId, days) => {
  const {data, error} = await adminWeightsControllerGetTraineeWeights({
    path: {traineeId: userId},
    ...(days ? {query: {days}} : {}),
  });
  if (error) throw error;
  return {data};
}

// TODO(openapi-migration): multipart file upload (FormData with an optional file blob +
// JSON metaData part) — stays on HttpService per the file-upload migration exception.
export const updateTrainee = async (trainee) => {
  const {data, error} = await adminTraineeControllerUpdateTrainee({body: trainee});
  if (error) throw error;
  return {data};
}

export const resetTraineePassword = async (traineeId, newPassword, forcePasswordChange) => {
  const {data, error} = await adminTraineeControllerResetPassword({
    path: {traineeId},
    body: {newPassword, forcePasswordChange},
  });
  if (error) throw error;
  return {data};
}

export const updateTraineeSettings = async (traineeId, settings) => {
  const {data, error} = await adminTraineeControllerUpdateTraineeSettings({
    path: {id: traineeId},
    body: settings,
  });
  if (error) throw error;
  return {data};
}

export const updateClientNote = async (body) => {
  const {data, error} = await adminTraineeNotesControllerUpdate({body});
  if (error) throw error;
  return {data};
}

export const updateTrainer = async (treaineId, trainerId) => {
  const {data, error} = await adminTraineeControllerUpdateTrainer({
    path: {traineeId: treaineId, trainerId},
  });
  if (error) throw error;
  return {data};
}

export const addSecondaryTrainer = async (traineeId, trainerId) => {
  const {data, error} = await adminTraineeControllerAddSecondaryTrainer({
    path: {traineeId, trainerId},
  });
  if (error) throw error;
  return {data};
}

export const removeSecondaryTrainer = async (traineeId, trainerId) => {
  const {data, error} = await adminTraineeControllerRemoveSecondaryTrainer({
    path: {traineeId, trainerId},
  });
  if (error) throw error;
  return {data};
}

export const saveClientNote = async (body) => {
  const {data, error} = await adminTraineeNotesControllerCreate({body});
  if (error) throw error;
  return {data};
}
export const getClientNotes = async (userId) => {
  const {data, error} = await adminTraineeNotesControllerGetAll({
    path: {traineeId: userId},
  });
  if (error) throw error;
  return {data};
};

export const getClientTargets = async (userId) => {
  const {data, error} = await adminTraineeTargetsControllerGetAll({
    path: {traineeId: userId},
  });
  if (error) throw error;
  return {data};
};

export const deleteTarget = async (noteId) => {
  const {data, error} = await adminTraineeTargetsControllerDelete({
    path: {id: noteId},
  });
  if (error) throw error;
  return {data};
};

export const saveClientTarget = async (body) => {
  const {data, error} = await adminTraineeTargetsControllerCreate({body});
  if (error) throw error;
  return {data};
}

export const updateClientTarget = async (body) => {
  const {data, error} = await adminTraineeTargetsControllerUpdate({body});
  if (error) throw error;
  return {data};
}

export const deleteNote = async (noteId) => {
  const {data, error} = await adminTraineeNotesControllerDelete({
    path: {id: noteId},
  });
  if (error) throw error;
  return {data};
};

export const deleteTrainee = async (id) => {
  const {data, error} = await adminTraineeControllerDeleteUser({path: {id}});
  if (error) throw error;
  return {data};
};

export const deleteTrainees = async (ids) => {
  const {data, error} = await adminTraineeControllerBulkDeleteTrainees({
    body: {traineeIds: ids},
  });
  if (error) throw error;
  return {data};
};

export const getNutritionCalculatorData = async (traineeId) => {
  const {data, error} = await adminTraineeControllerGetNutritionCalculatorData({
    path: {id: traineeId},
  });
  if (error) throw error;
  return {data};
};

export const importTrainees = async (request) => {
  const {data, error} = await adminTraineeControllerImportTrainees({body: request});
  if (error) throw error;
  return {data};
};

export const getLiveClients = async (page, pageSize, keyword, trainerId, gender, state, companyId, sortBy, sortDirection) => {
  // Preserve the legacy conditional query-building (skip empty keyword, skip trainer/gender/
  // state falsy or 'ALL', etc.). companyId dropped — endpoint no longer accepts it.
  const query = {page, pageSize};
  if (keyword) query.keyword = keyword;
  if (trainerId) query.trainerId = trainerId;
  if (gender && gender !== "ALL") query.gender = gender;
  if (state && state !== "ALL") query.state = state;
  if (sortBy) query.sortBy = sortBy;
  if (sortDirection) query.sortDirection = sortDirection;

  const {data, error} = await adminClientReviewControllerGetLiveClientOverview({query});
  if (error) throw error;
  return {data};
};

export const getEnhancedStats = async (companyId, expiringSoonDays = null) => {
  // companyId dropped — endpoint no longer takes it (derived from principal).
  const query = {};
  if (expiringSoonDays != null) query.expiringSoonDays = expiringSoonDays;
  const {data, error} = await adminTraineeControllerGetEnhancedStatistics({query});
  if (error) throw error;
  return {data};
};
